import { File, Paths } from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';
import Papa from 'papaparse';
import Toast from 'react-native-toast-message';

/**
 * DataExporter - Cross-platform utility for exporting JSON data to CSV, Excel, and PDF formats
 * 
 * Supports iOS, Android, and Web platforms with native file picker/sharing capabilities
 * 
 * @example
 * const data = [
 *   { name: 'John', age: 30, email: 'john@example.com' },
 *   { name: 'Jane', age: 25, email: 'jane@example.com' }
 * ];
 * 
 * // Export to CSV
 * await DataExporter.exportToCSV(data, 'users');
 * 
 * // Export to Excel
 * await DataExporter.exportToExcel(data, 'users');
 * 
 * // Export to PDF
 * await DataExporter.exportToPDF(data, 'users');
 */
class DataExporter {
  /**
   * Export JSON array to CSV format
   * @param {Array<Object>} data - Array of objects to export
   * @param {string} filename - Base filename (without extension)
   * @param {Object} options - Additional options
   * @param {Array<string>} options.columns - Specific columns to export (optional)
   * @param {Object} options.headers - Custom header labels (optional)
   * @returns {Promise<boolean>} Success status
   */
  static async exportToCSV(data, filename = 'export', options = {}) {
    try {
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        Toast.show({type: 'error', text2: 'No data available to export'})
        return false;
      }

      // Filter columns if specified
      let exportData = data;
      if (options.columns && options.columns.length > 0) {
        exportData = data.map(row => {
          const filtered = {};
          options.columns.forEach(col => {
            filtered[col] = row[col];
          });
          return filtered;
        });
      }

      // Apply custom headers if provided
      if (options.headers) {
        exportData = exportData.map(row => {
          const renamed = {};
          Object.keys(row).forEach(key => {
            const newKey = options.headers[key] || key;
            renamed[newKey] = row[key];
          });
          return renamed;
        });
      }

      // Convert to CSV using PapaParse
      const csv = Papa.unparse(exportData, {
        quotes: true,
        header: true,
        skipEmptyLines: true
      });

      return await this._saveAndShare(csv, `${filename}.csv`, 'text/csv');
    } catch (error) {
      console.error('CSV Export Error:', error);
      throw error;
    }
  }

  /**
   * Export JSON array to Excel format (XLSX)
   * @param {Array<Object>} data - Array of objects to export
   * @param {string} filename - Base filename (without extension)
   * @param {Object} options - Additional options
   * @param {Array<string>} options.columns - Specific columns to export (optional)
   * @param {Object} options.headers - Custom header labels (optional)
   * @param {string} options.sheetName - Name of the Excel sheet (default: 'Sheet1')
   * @returns {Promise<boolean>} Success status
   */
  static async exportToExcel(data, filename = 'export', options = {}) {
    try {
      if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error('Data must be a non-empty array');
      }

      // For web platform, use xlsx library
      if (Platform.OS === 'web') {
        return await this._exportToExcelWeb(data, filename, options);
      }

      // For mobile platforms, use xlsx library
      return await this._exportToExcelNative(data, filename, options);
    } catch (error) {
      console.error('Excel Export Error:', error);
      throw error;
    }
  }

  /**
   * Export JSON array to PDF format (as a table)
   * @param {Array<Object>} data - Array of objects to export
   * @param {string} filename - Base filename (without extension)
   * @param {Object} options - Additional options
   * @param {Array<string>} options.columns - Specific columns to export (optional)
   * @param {Object} options.headers - Custom header labels (optional)
   * @param {string} options.title - Document title (optional)
   * @param {string} options.orientation - 'portrait' or 'landscape' (default: 'portrait')
   * @returns {Promise<boolean>} Success status
   */
  static async exportToPDF(data, filename = 'export', options = {}) {
    try {
      if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error('Data must be a non-empty array');
      }

      // For web platform, use jsPDF and jspdf-autotable
      if (Platform.OS === 'web') {
        return await this._exportToPDFWeb(data, filename, options);
      }

      // For mobile platforms, use react-native-html-to-pdf
      return await this._exportToPDFNative(data, filename, options);
    } catch (error) {
      console.error('PDF Export Error:', error);
      throw error;
    }
  }

  /**
   * Helper: Export to Excel on web platform
   * @private
   */
  static async _exportToExcelWeb(data, filename, options) {
    // Dynamic import for web-only library
    const XLSX = await import('xlsx');
    
    let exportData = this._prepareData(data, options);
    
    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, options.sheetName || 'Sheet1');
    
    // Generate Excel file and trigger download
    XLSX.writeFile(wb, `${filename}.xlsx`);
    
    return true;
  }

  /**
   * Helper: Export to Excel on native platforms
   * @private
   */
  static async _exportToExcelNative(data, filename, options) {
    // Dynamic import for native compatibility
    const XLSX = await import('xlsx');
    
    let exportData = this._prepareData(data, options);
    
    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, options.sheetName || 'Sheet1');
    
    // Generate binary string
    const wbout = XLSX.write(wb, { type: 'base64', bookType: 'xlsx' });
    
    return await this._saveAndShare(wbout, `${filename}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', true);
  }

  /**
   * Helper: Export to PDF on web platform
   * @private
   */
  static async _exportToPDFWeb(data, filename, options) {
    // Dynamic imports for web-only libraries
    const { jsPDF } = await import('jspdf');
    const autoTableModule = await import('jspdf-autotable');

    // Support both default and named exports from jspdf-autotable
    const autoTable =
      autoTableModule.default || autoTableModule.autoTable || autoTableModule.jsPDF?.autoTable;

    if (!autoTable) {
      throw new Error('Failed to load jspdf-autotable. Check that the package is correctly installed.');
    }

    let exportData = this._prepareData(data, options);

    // Create PDF document
    const doc = new jsPDF({
      orientation: options.orientation || 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    let tableStartY = 15;

    // Add title if provided
    if (options.title) {
      doc.setFontSize(16);
      doc.setTextColor(35, 42, 51);
      doc.text(options.title, 14, 15);
      tableStartY = 22;
    }

    if (options.subtitle) {
      doc.setFontSize(8);
      doc.setTextColor(100, 110, 122);
      const subtitleLines = doc.splitTextToSize(String(options.subtitle), 182);
      doc.text(subtitleLines, 14, tableStartY);
      tableStartY += subtitleLines.length * 3.5 + 3;
    }

    // Extract headers and rows
    const headers = Object.keys(exportData[0] || {});
    const rows = exportData.map(row => headers.map(header => row[header]));

    // Generate table using the imported autoTable function
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: tableStartY,
      theme: options.tableTheme || 'grid',
      styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak', ...options.tableStyles },
      headStyles: { fillColor: [66, 139, 202], textColor: 255, ...options.headStyles },
      alternateRowStyles: options.alternateRowStyles,
      columnStyles: options.columnStyles,
      margin: options.margin
    });

    if (options.summary) {
      const pageHeight = doc.internal.pageSize.getHeight();
      let summaryY = (doc.lastAutoTable?.finalY || tableStartY) + 8;
      if (summaryY > pageHeight - 24) {
        doc.addPage();
        summaryY = 15;
      }

      doc.setFontSize(12);
      doc.setTextColor(35, 42, 51);
      doc.text(options.summary.title || 'Summary', 14, summaryY);
      summaryY += 4;

      const sections = [
        { title: null, items: options.summary.items || [] },
        ...(options.summary.sections || []),
      ];

      sections.forEach(section => {
        if (!section.items?.length) return;
        if (summaryY > pageHeight - 20) {
          doc.addPage();
          summaryY = 15;
        }
        autoTable(doc, {
          head: section.title ? [[section.title, 'Amount']] : undefined,
          body: section.items.map(item => [item.label, item.value]),
          startY: summaryY,
          theme: 'plain',
          styles: { fontSize: 8, cellPadding: 1.4, overflow: 'linebreak' },
          headStyles: { fillColor: [230, 244, 234], textColor: [5, 120, 83], fontStyle: 'bold' },
          columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 38, halign: 'right', fontStyle: 'bold' } },
          margin: { left: 14, right: 14 }
        });
        summaryY = (doc.lastAutoTable?.finalY || summaryY) + 3;
      });
    }

    // Save PDF
    doc.save(`${filename}.pdf`);

    return true;
  }

  /**
   * Helper: Export to PDF on native platforms
   * @private
   */
  static async _exportToPDFNative(data, filename, options) {
    // Dynamic import for native compatibility
    const RNHTMLtoPDF = await import('react-native-html-to-pdf');
    
    let exportData = this._prepareData(data, options);
    const escapeHTML = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
    const renderValue = (value) => escapeHTML(value).replace(/\r?\n/g, '<br>');
    
    // Generate HTML table
    const headers = Object.keys(exportData[0] || {});
    const configuredWidths = headers.map((_header, index) => Number(options.columnStyles?.[index]?.cellWidth) || 1);
    const totalWidth = configuredWidths.reduce((sum, width) => sum + width, 0);
    const summarySections = [
      { title: null, items: options.summary?.items || [] },
      ...(options.summary?.sections || []),
    ];
    const summaryHTML = options.summary ? `
      <section class="summary">
        <h2>${escapeHTML(options.summary.title || 'Summary')}</h2>
        ${summarySections.map(section => section.items?.length ? `
          <div class="summary-section">
            ${section.title ? `<h3>${escapeHTML(section.title)}</h3>` : ''}
            ${section.items.map(item => `<div class="summary-row"><span>${escapeHTML(item.label)}</span><strong>${escapeHTML(item.value)}</strong></div>`).join('')}
          </div>
        ` : '').join('')}
      </section>
    ` : '';
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            @page { size: A4 portrait; margin: 0; }
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 18px; color: #232a33; font-size: 7px; }
            h1 { color: #232a33; font-size: 18px; margin: 0 0 3px; }
            .subtitle { color: #64707c; font-size: 8px; margin: 0 0 10px; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            thead { display: table-header-group; }
            th { background-color: #059669; color: white; padding: 4px 3px; text-align: left; border: 0.5px solid #d9e2dd; font-size: 7px; }
            td { padding: 3px; border: 0.5px solid #e3e8e5; vertical-align: top; overflow-wrap: anywhere; word-break: break-word; white-space: normal; line-height: 1.25; }
            tr:nth-child(even) { background-color: #f6f8f7; }
            .summary { margin-top: 14px; }
            .summary h2 { font-size: 13px; margin: 0 0 5px; }
            .summary-section { width: 58%; margin: 0 0 7px; break-inside: avoid; }
            .summary-section h3 { color: #057853; background-color: #e6f4ea; font-size: 8px; margin: 0; padding: 3px 4px; }
            .summary-row { display: flex; justify-content: space-between; gap: 14px; padding: 2px 4px; border-bottom: 0.5px solid #edf0ee; font-size: 8px; }
            .summary-row strong { text-align: right; }
          </style>
        </head>
        <body>
          ${options.title ? `<h1>${escapeHTML(options.title)}</h1>` : ''}
          ${options.subtitle ? `<p class="subtitle">${escapeHTML(options.subtitle)}</p>` : ''}
          <table>
            <colgroup>${headers.map((_header, index) => `<col style="width:${(configuredWidths[index] / totalWidth) * 100}%">`).join('')}</colgroup>
            <thead>
              <tr>${headers.map((header, index) => `<th style="text-align:${options.columnStyles?.[index]?.halign || 'left'}">${escapeHTML(header)}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${exportData.map(row => 
                `<tr>${headers.map((header, index) => `<td style="text-align:${options.columnStyles?.[index]?.halign || 'left'}">${renderValue(row[header])}</td>`).join('')}</tr>`
              ).join('')}
            </tbody>
          </table>
          ${summaryHTML}
        </body>
      </html>
    `;
    
    // Generate PDF from HTML
    const pdfOptions = {
      html: htmlContent,
      fileName: filename,
      directory: 'Documents',
      width: 595,
      height: 842,
      padding: 0,
      base64: true
    };
    
    const file = await RNHTMLtoPDF.generatePDF(pdfOptions);
    
    // Share the generated PDF
    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(file.filePath, {
        mimeType: 'application/pdf',
        dialogTitle: 'Save PDF'
      });
    }
    
    return true;
  }

  /**
   * Helper: Prepare data with column filtering and header renaming
   * @private
   */
  static _prepareData(data, options) {
    let exportData = [...data];
    
    // Filter columns if specified
    if (options.columns && options.columns.length > 0) {
      exportData = exportData.map(row => {
        const filtered = {};
        options.columns.forEach(col => {
          filtered[col] = row[col];
        });
        return filtered;
      });
    }
    
    // Apply custom headers if provided
    if (options.headers) {
      exportData = exportData.map(row => {
        const renamed = {};
        Object.keys(row).forEach(key => {
          const newKey = options.headers[key] || key;
          renamed[newKey] = row[key];
        });
        return renamed;
      });
    }
    
    return exportData;
  }

  /**
   * Helper: Save file and trigger sharing/download
   * @private
   */
  static async _saveAndShare(content, filename, mimeType, isBase64 = false) {
    if (Platform.OS === 'web') {
      // Web: Create blob and trigger download
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } else {
      // Mobile: Save to file system and share
      const newFile = new File(Paths.document, filename);
      
      if(!newFile.exists){
        newFile.create()
      }

      newFile.write(content)
      
      // Share the file (allows user to choose save location)
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(newFile.uri, {
          mimeType: mimeType,
          dialogTitle: `Save ${filename}`
        });
      } else {
        throw new Error('Sharing is not available on this device');
      }
      
      return true;
    }
  }

  /**
   * Batch export to multiple formats
   * @param {Array<Object>} data - Array of objects to export
   * @param {string} filename - Base filename (without extension)
   * @param {Array<string>} formats - Array of formats: ['csv', 'excel', 'pdf']
   * @param {Object} options - Export options
   * @returns {Promise<Object>} Results for each format
   */
  static async exportToMultiple(data, filename = 'export', formats = ['csv'], options = {}) {
    const results = {};
    
    for (const format of formats) {
      try {
        switch (format.toLowerCase()) {
          case 'csv':
            results.csv = await this.exportToCSV(data, filename, options);
            break;
          case 'excel':
          case 'xlsx':
            results.excel = await this.exportToExcel(data, filename, options);
            break;
          case 'pdf':
            results.pdf = await this.exportToPDF(data, filename, options);
            break;
          default:
            results[format] = { error: `Unknown format: ${format}` };
        }
      } catch (error) {
        results[format] = { error: error.message };
      }
    }
    
    return results;
  }
}

export default DataExporter;
